// EventSort.tsx
import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ais-ui/ui-core';
import {
  PiCalendarBlank,
  PiSortAscending,
  PiSortDescending,
  PiWarning,
} from 'react-icons/pi';

import { SortButton } from '../../components/SortButton';
import { EventSortConfig, EventSortType } from './types';

interface EventSortProps {
  sortConfig: EventSortConfig;
  setSortConfig: React.Dispatch<React.SetStateAction<EventSortConfig>>;
}

const getSortIcon = (type: EventSortType) => {
  switch (type) {
    case 'date':
      return PiCalendarBlank;
    case 'severity':
      return PiWarning;
    default:
      return PiSortAscending;
  }
};

const getSortLabel = (type: EventSortType) => {
  switch (type) {
    case 'date':
      return 'Date';
    case 'severity':
      return 'Severity';
    default:
      return 'Sort';
  }
};

export const EventSort = ({ sortConfig, setSortConfig }: EventSortProps) => {
  const handleSortChange = (
    type: EventSortType,
    direction?: 'asc' | 'desc',
  ) => {
    setSortConfig({
      type,
      direction:
        direction ||
        (type === sortConfig.type && sortConfig.direction === 'asc'
          ? 'desc'
          : 'asc'),
    });
  };

  const toggleDirection = () => {
    setSortConfig((prev) => ({
      ...prev,
      direction: prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SortButton
          direction={sortConfig.direction}
          label={getSortLabel(sortConfig.type)}
          hideLabel={true}
          asChild={true}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
          Sort by
        </div>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => handleSortChange('date')}
          className={sortConfig.type === 'date' ? 'bg-accent' : ''}
        >
          <PiCalendarBlank className="mr-2 size-4" />
          Date
          {sortConfig.type === 'date' && (
            <div className="ml-auto">
              {sortConfig.direction === 'asc' ? (
                <PiSortAscending className="size-4" />
              ) : (
                <PiSortDescending className="size-4" />
              )}
            </div>
          )}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handleSortChange('severity')}
          className={sortConfig.type === 'severity' ? 'bg-accent' : ''}
        >
          <PiWarning className="mr-2 size-4" />
          Severity
          {sortConfig.type === 'severity' && (
            <div className="ml-auto">
              {sortConfig.direction === 'asc' ? (
                <PiSortAscending className="size-4" />
              ) : (
                <PiSortDescending className="size-4" />
              )}
            </div>
          )}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={toggleDirection}>
          <div className="flex items-center">
            {sortConfig.direction === 'asc' ? (
              <>
                <PiSortDescending className="mr-2 size-4" />
                Switch to descending
              </>
            ) : (
              <>
                <PiSortAscending className="mr-2 size-4" />
                Switch to ascending
              </>
            )}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
